import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useNavigate } from "react-router-dom";
import { FiSearch, FiEye, FiAlertTriangle } from "react-icons/fi";

export default function StockIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alertModal, setAlertModal] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        fetchStock();
    }, []);

    const fetchStock = () => {
        setLoading(true);

        const token = localStorage.getItem("authToken");

        axios.get(`${process.env.REACT_APP_API_URL}/stock`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
            .then(res => {
                if (res.data.status) {
                    setData(res.data.stock || []);
                    setCategories(res.data.categories || []);
                } else {
                    setData([]);
                    setCategories([]);
                }
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    const handleLowStockAlert = (product) => {
        setCurrentProduct(product);
        setAlertModal(true);
    };

    const handleViewSales = (productId) => {
        navigate(`/stock/show/${productId}`);
    };

    // Filter data by search term and category
    const filteredData = data.filter(item => {
        const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory ? item.category_id == selectedCategory : true;
        return matchesSearch && matchesCategory;
    });

    if (loading) return <PageLayout><div className="text-center p-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t("loading")}...</span></div><p className="mt-3">{t("loading")}...</p></div></PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb p-3">
                            <h3 className="mc-breadcrumb-title mb-0">{t('stock management')}</h3>
                            <ul className="mc-breadcrumb-list mb-0">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('inventory')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('stock')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {/* Header with Search and Filter */}
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-3">
                            <h5 className="mb-0">{t('stock list')}</h5>
                            <div className="d-flex gap-3 flex-wrap">
                                {/* Category Filter */}
                                <Form.Select
                                    size="sm"
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    style={{ width: '200px' }}
                                >
                                    <option value="">{t('All categories')}</option>
                                    {categories.map(category => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </Form.Select>

                                {/* Search Input */}
                                <div className="position-relative">
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder={t('search products...')}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ paddingRight: '35px', width: '250px' }}
                                    />
                                    <FiSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" size={16} />
                                </div>
                            </div>
                        </div>

                        <div className="table-responsive-wrapper">
                            <Table responsive hover className="table mb-0">
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: '5%' }}>#</th>
                                        <th style={{ width: '20%' }}>{t('product name')}</th>
                                        <th style={{ width: '15%' }}>{t('category')}</th>
                                        <th className="text-center" style={{ width: '12%' }}>{t('available stock')}</th>
                                        <th className="text-center" style={{ width: '13%' }}>{t('status')}</th>
                                        <th className="text-center" style={{ width: '15%' }}>{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.length > 0 ? (
                                        filteredData.map((item, index) => (
                                            <tr key={item.id}>
                                                <td className="text-center">{index + 1}</td>
                                                
                                                <td>
                                                    <strong> {item.name}</strong> - {item.vendor || '-'}
                                                </td>
                                                <td>
                                                    <span className="badge bg-light text-dark">
                                                        {item.category_name}
                                                    </span>
                                                </td>
                                                
                                                <td className="text-center">
                                                    <span className="badge bg-info text-white px-3 py-2">
                                                        {item.available_stock}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    {item.low_stock ? (
                                                        <span
                                                            className="badge bg-danger px-3 py-2"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => handleLowStockAlert(item)}
                                                        >
                                                            {t('low stock')}
                                                        </span>
                                                    ) : (
                                                        <span className="badge bg-success px-3 py-2">
                                                            {t('in stock')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <div className="mc-table-action justify-content-center">
                                                        <button
                                                            className="material-icons view"
                                                            onClick={() => handleViewSales(item.id)}
                                                            title={t('View Sales History')}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <FiEye />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5">
                                                <div className="empty-state">
                                                    <FiAlertTriangle size={48} className="text-muted mb-2" />
                                                    <p className="text-muted mb-0">{t('no stock data found')}</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Low Stock Alert Modal */}
            <Modal show={alertModal} onHide={() => setAlertModal(false)} centered>
                <Modal.Body className="text-center p-4">
                    <FiAlertTriangle size={50} className="text-warning mb-3" />
                    <h4 className="mb-3">{t("Low Stock Alert")}</h4>
                    <p>
                        <strong>{currentProduct?.name}</strong> {t("has only")} <strong>{currentProduct?.available_stock}</strong> {t("items left in stock!")}
                    </p>
                    <p className="text-muted small">{t("Please restock this product soon.")}</p>
                    <div className="d-flex justify-content-center gap-2 mt-4">
                        <ButtonComponent
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setAlertModal(false)}
                            text={t('ok')}
                        />
                    </div>
                </Modal.Body>
            </Modal>
        </PageLayout>
    );
}